<template>
<div>
	<section class="dashboard detail product">
		<nav>
			<div></div>
			<div class="right"><div :class="['button', section]" @click="backToReport()">Back to {{ section }} {{ section == 'dashboard' ? '' : 'Report' }}</div></div>
		</nav>
		<nav class="top">
			<h1>{{ productDetail.short_description }}</h1>
			<div class="meta">
				<div></div>
				<div>
					<label>Start</label><div class="meta-data">{{ formatDate(totals.start_date) }}</div>
				</div>
				<div>
					<label>End</label><div class="meta-data">{{ formatDate(totals.end_date) }}</div>
				</div>
			</div>
		</nav>

		<section v-if="productDetail.product_id" class="product-detail">
			<div class="product-image">
				<img :src="productDetail.image_src" />
				<div class="meta">ID: {{ product_id }}</div>
			</div>
			<div>
				<h3 class="underline">Product</h3>
				<label>UPC</label>{{ productDetail.upc }}
				<label>Manufacturer</label>{{ productDetail.manufacturer }}
				<label>Model</label>{{ productDetail.model }}
				<label>SKU</label>{{ productDetail.sku }}
				<label>Caliber</label><span v-if="productDetail.caliber">{{ productDetail.caliber }}</span><span v-else>N/A</span>
				<label>MAP</label><span v-if="productDetail.map">${{ productDetail.map }}</span><span v-else>N/A</span>
				<label>MSRP</label><span v-if="productDetail.msrp">${{ productDetail.msrp }}</span><span v-else>N/A</span>
			</div>
			<div>
				<h3 class="underline">Dealers</h3>
				<label>Qty On Hand</label>{{ formatNumber(productDetail.on_hand) }}
				<label>Cost On Hand</label>{{ formatCurrency(productDetail.cost_on_hand) }}
				<label>Retail On Hand</label>{{ formatCurrency(productDetail.price_on_hand) }}
				<label>Average Cost</label>
				<span>{{ formatCurrency(productDetail.average_dealer_cost) }}</span>
				<label>Average Retail</label>
				<span>{{ formatCurrency(productDetail.average_dealer_price) }}</span>
				<label>Average Margin</label>
				<span v-if="productDetail.average_margin">{{ productDetail.average_margin }}%</span><span v-else>N/A</span>
				<label>GMROI</label>
				{{ productDetail.gmroi }}
			</div>
			<div>
				<h3 class="underline">Sales</h3>
				<label>Qty Sold</label>
				{{ formatNumber(productDetail.sold) }}
				<label>Cost Sold</label>
				{{ formatCurrency(productDetail.cost_sold) }}
				<label>Price Sold</label>
				{{ formatCurrency(productDetail.price_sold) }}
				<label>Profit Sold</label>
				{{ formatCurrency(productDetail.profit_sold) }}
				<label>% Department</label>
				{{ productDetail.percent_of_department_sales }}%
			</div>
			<div>
				<h3 class="underline">Distributors</h3>
				<div v-if="productDetail.distributors.length > 0">
					<div v-for="(distributor, index) in productDetail.distributors" :key="'distributor_' + index" style="margin: 0 0 0.5em 0;">
						<label>{{ distributor.description }}</label>
						{{ formatNumber(distributor.quantity) }} @ {{ formatCurrency(distributor.cost) }}
						<span class="meta">({{ distributor.days_out_of_stock }} / {{ distributor.percent_out_of_stock }}%)</span>
					</div>
					<div style="font-size: 0.8em; color: #999; margin: 1em 0 0.5em 0;">Qty @ Cost (days / % out of stock)</div>
					<div class="divider"></div>
					<label>Distributor High / Low</label>
					{{ formatCurrency(productDetail.distributor_max_cost) }} / {{ formatCurrency(productDetail.distributor_min_cost) }}
				</div>
				<div v-else>N/A</div>
			</div>
		</section>

		<section v-if="productDetail.product_id" class="product-detail-charts two-columns">
			<div class="product-detail-chart product">
				<h3>On-hand inventory {{ trendNumberOfDays }} day trend<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('inventory_trend')" /></h3>
				<div class="chart-wrapper">
					<div ref="chart_inventory_trend" class="chart" style="height: 160px;"></div>
					<div v-if="!productStats.inventory_trend || productStats.inventory_trend.length == 0" class="no-data">No Data</div>
					<div v-if="showLegend == 'inventory_trend'" class="product-detail-legend" @click="toggleLegend('inventory_trend')">{{ legends['inventory_trend'] }}</div>
				</div>
			</div>
			<div class="product-detail-chart product">
				<h3>Sales {{ trendNumberOfDays }} day trend<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('sales_trend')" /></h3>
				<div class="chart-wrapper">
					<div ref="chart_sales_trend" class="chart" style="height: 160px;"></div>
					<div v-if="!productStats.sales_trend || productStats.sales_trend.length == 0" class="no-data">No Data</div>
					<div v-if="showLegend == 'sales_trend'" class="product-detail-legend" @click="toggleLegend('sales_trend')">{{ legends['sales_trend'] }}</div>
				</div>
			</div>
		</section>

		<section v-if="productDetail.product_id" class="product-detail-charts two-columns">
			<div class="product-detail-chart product">
				<h3>Inventory and sales monthly<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('inventory_sales_monthly')" /></h3>
				<div class="chart-wrapper">
					<div ref="chart_inventory_sales_monthly" class="chart" style="height: 260px;"></div>
					<div v-if="!productStats.inventory_sales_monthly || productStats.inventory_sales_monthly.length == 0" class="no-data">No Data</div>
					<div v-if="showLegend == 'inventory_sales_monthly'" class="product-detail-legend" @click="toggleLegend('inventory_sales_monthly')">{{ legends['inventory_sales_monthly'] }}</div>
				</div>
			</div>
			<div class="product-detail-chart product">
				<h3>Top Suppliers<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('suppliers')" /></h3>
				<div class="chart-wrapper">
					<div ref="chart_suppliers" class="chart" style="height: 260px;"></div>
					<div v-if="!productStats.suppliers || productStats.suppliers.length == 0" class="no-data">No Data</div>
					<div v-if="showLegend == 'suppliers'" class="product-detail-legend" @click="toggleLegend('suppliers')">{{ legends['suppliers'] }}</div>
				</div>
			</div>
		</section>

		<section v-if="productDetail.product_id" class="product-detail-charts three-columns">
			<div class="product-detail-chart product">
				<h3>Store On Hand<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('dealer_stock')" /></h3>
				<div class="chart-wrapper">
					<div ref="chart_dealer_stock" class="chart" style="height: 200px;"></div>
					<div v-if="!productStats.dealer_stock || productStats.dealer_stock.length == 0" class="no-data">No Data</div>
					<div v-if="showLegend == 'dealer_stock'" class="product-detail-legend" @click="toggleLegend('dealer_stock')">{{ legends['dealer_stock'] }}</div>
				</div>
			</div>
			<div class="product-detail-chart product">
				<h3>Warehouse Orders<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('warehouse_orders')" /></h3>
				<div class="chart-wrapper">
					<div ref="chart_warehouse_orders" class="chart" style="height: 200px;"></div>
					<div v-if="!productStats.warehouse_orders || productStats.warehouse_orders.length == 0" class="no-data">No Data</div>
					<div v-if="showLegend == 'warehouse_orders'" class="product-detail-legend" @click="toggleLegend('warehouse_orders')">{{ legends['warehouse_orders'] }}</div>
				</div>
			</div>
			<div class="product-detail-chart product">
				<h3>Sales by type<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('sales_by_type')" /></h3>
				<div class="chart-wrapper">
					<div ref="chart_sales_by_type" class="chart" style="height: 200px;"></div>
					<div v-if="!productStats.sales_by_type || productStats.sales_by_type.length == 0" class="no-data">No Data</div>
					<div v-if="showLegend == 'sales_by_type'" class="product-detail-legend" @click="toggleLegend('sales_by_type')">{{ legends['sales_by_type'] }}</div>
				</div>
			</div>

		</section>

		<div v-if="productDetail.product_id" style="margin: 1em 0;">
			<h3>Inventory Dealers</h3>
			<div v-if="loadingDealerList" class="loading-list"><font-awesome-icon icon="fa-spinner" class="icon fa-spin" /></div>
			<table v-else class="list">
				<tr>
					<th class="left">Dealer</th>
					<th :class="['right', 'sortable-column', section]" @click="setSort('cost_each')">
						<font-awesome-icon v-if="filters.dealer_sort_column == 'cost_each'" :icon="filters.dealer_sort_order == 'desc' ? 'fa-caret-down' : 'fa-caret-up'" class="icon" /><br>
						Cost Each
					</th>
					<th :class="['right', 'sortable-column', section]" @click="setSort('price_each')">
						<font-awesome-icon v-if="filters.dealer_sort_column == 'price_each'" :icon="filters.dealer_sort_order == 'desc' ? 'fa-caret-down' : 'fa-caret-up'" class="icon" /><br>
						Price Each
					</th>
					<th :class="['right', 'sortable-column', section]" @click="setSort('on_hand')">
						<font-awesome-icon v-if="filters.dealer_sort_column == 'on_hand'" :icon="filters.dealer_sort_order == 'desc' ? 'fa-caret-down' : 'fa-caret-up'" class="icon" /><br>
						On Hand
					</th>
					<th :class="['right', 'sortable-column', section]" @click="setSort('cost_on_hand')">
						<font-awesome-icon v-if="filters.dealer_sort_column == 'cost_on_hand'" :icon="filters.dealer_sort_order == 'desc' ? 'fa-caret-down' : 'fa-caret-up'" class="icon" /><br>
						Cost On Hand
					</th>
					<th :class="['right', 'sortable-column', section]" @click="setSort('price_on_hand')">
						<font-awesome-icon v-if="filters.dealer_sort_column == 'price_on_hand'" :icon="filters.dealer_sort_order == 'desc' ? 'fa-caret-down' : 'fa-caret-up'" class="icon" /><br>
						Price On Hand
					</th>
					<th :class="['right', 'sortable-column', section]" @click="setSort('sold')">
						<font-awesome-icon v-if="filters.dealer_sort_column == 'sold'" :icon="filters.dealer_sort_order == 'desc' ? 'fa-caret-down' : 'fa-caret-up'" class="icon" /><br>
						Sold
					</th>
					<th :class="['right', 'sortable-column', section]" @click="setSort('cost_sold')">
						<font-awesome-icon v-if="filters.dealer_sort_column == 'cost_sold'" :icon="filters.dealer_sort_order == 'desc' ? 'fa-caret-down' : 'fa-caret-up'" class="icon" /><br>
						Cost Sold
					</th>
					<th :class="['right', 'sortable-column', section]" @click="setSort('price_sold')">
						<font-awesome-icon v-if="filters.dealer_sort_column == 'price_sold'" :icon="filters.dealer_sort_order == 'desc' ? 'fa-caret-down' : 'fa-caret-up'" class="icon" /><br>
						Price Sold
					</th>
					<th class="right">Turn</th>
					<th class="right">GMROI</th>
				</tr>
				<tr v-for="(dealer, index) in dealerList" :index="dealer.location_id" class="clickable" @click="goToDealer(dealer)">
					<td :class="['left', section]"><div style="padding: 0.25em;">{{ dealer.company_name }}<div class="meta">{{ dealer.address }}</div></div></td>
					<td :class="['right', section]">{{ formatCurrency(dealer.cost_each) }}</td>
					<td :class="['right', section]">{{ formatCurrency(dealer.price_each) }}</td>
					<td :class="['right', section]">{{ formatNumber(dealer.on_hand) }}</td>
					<td :class="['right', section]">{{ formatCurrency(dealer.cost_on_hand) }}</td>
					<td :class="['right', section]">{{ formatCurrency(dealer.price_on_hand) }}</td>
					<td :class="['right', section]">{{ dealer.sold ? formatNumber(dealer.sold) : '' }}</td>
					<td :class="['right', section]">{{ formatCurrency(dealer.cost_sold) }}</td>
					<td :class="['right', section]">{{ formatCurrency(dealer.price_sold) }}</td>
					<td :class="['right', section]">{{ dealer.turnover }}</td>
					<td :class="['right', section]">{{ dealer.gmroi }}</td>
				</tr>
			</table>
			<nav class="bottom">
				<div></div>
				<pagination :paging="dealerListPaging" :section="section" @updatePage="goToDealerListPage" />
			</nav>
		</div>

		<div v-if="productDetail.product_id" class="dashboard" style="margin: 1em 0;">
			<h3>Non-Inventory Dealers</h3>
			<div v-if="loadingNonStockingDealerList" class="loading-list"><font-awesome-icon icon="fa-spinner" class="icon fa-spin" /></div>
			<table v-else class="list">
				<tr>
					<th class="left">Dealer</th>
				</tr>
				<tr v-for="(dealer, index) in nonStockingDealerList" :index="dealer.location_id" class="clickablez">
					<td :class="['left', section]"><div style="padding: 0.25em 0;">
						{{ dealer.company_name }}
						<span class="meta">{{ dealer.address }}</span>
					</div></td>
				</tr>
			</table>
			<nav class="bottom">
				<div></div>
				<pagination :paging="nonStockingDealerListPaging" :section="section" @updatePage="goToNonStockingDealerListPage" />
			</nav>
		</div>

		<message-modal v-if="message" :section="section" :message="message" :loading="loadingProduct" />

		<nav class="bottom">
			<div></div>
			<div class="right"><div :class="['button', section]" @click="backToReport()">Back to {{ section }} Report</div></div>
		</nav>

	</section>

	<dealer v-if="locationId" :location_id="locationId" :section="section" @close="closeDealer" :key="'location_' + locationId"/>

</div>
</template>

<script>
	export default {
		name: 'product',
		props: ['section', 'product_id'],
		data() {
			return {
				filters: {},
				loadingProduct: false,
				productDetail: {},
				loadingStats: false,
				totals: {},
				productStats: {},
				trendNumberOfDays: '',
				backToReportPath: '',
				loadingDealerList: false,
				dealerList: [],
				dealerListPaging: {},
				loadingNonStockingDealerList: false,
				nonStockingDealerList: [],
				nonStockingDealerListPaging: {},
				message: '',
				legends: {},
				showLegend: '',
				locationId: ''
			}
		},
		created() {
			this.updateSource('product')
			if (sessionStorage.getItem('filters')) {
				this.filters = JSON.parse(sessionStorage.getItem('filters'))
				// always reset the page number and product_id
				this.filters.section = this.section
				this.filters.product_id = this.product_id
				this.filters.dealer_list_page = 1
				this.filters.non_stocking_dealer_list_page = 1
				sessionStorage.setItem('filters', JSON.stringify(this.filters));
			}
			this.getProduct()
			this.backToReportPath = '/' + this.section
		},
		methods: {
			getProduct() {
				if (this.loadingProduct) { return }
				this.loadingProduct = true
				this.apiCall({ destination: 'get_product_detail', data: this.filters }).then(obj => {
					if (obj.status == 'success') {
						this.loadingProduct = false
						this.totals = obj.totals
						this.productDetail = obj.detail
						this.getChartData()
					}
				}).catch(err => {
					console.log(err)
				})
			},
			getChartData() {
				if (this.loadingStats) { return }
				this.loadingStats = true
				this.apiCall({ destination: 'get_product_detail_stats', data: this.filters }).then(obj => {
					if (obj.status == 'success') {
						this.loadingStats = false
						this.productStats = obj.stats
						this.legends = obj.legends
						this.trendNumberOfDays = obj.trend_number_of_days
						this.getDealerList()
						this.getNonStockingDealerList()
						// slight delay so the refs will be mounted in dom
						setTimeout(() => {
							this.renderCharts()
						}, 100)
					}
				}).catch(err => {
					console.log(err)
				})
			},
			renderCharts() {
				if (this.productStats.inventory_trend && this.productStats.inventory_trend.length > 0) {
					this.createLineChart(this.$refs.chart_inventory_trend, this.productStats.inventory_trend, this.getSectionColors.inventory)
				}
				if (this.productStats.sales_trend && this.productStats.sales_trend.length > 0) {
					this.createLineChart(this.$refs.chart_sales_trend, this.productStats.sales_trend, this.getSectionColors.sales)
				}
				if (this.productStats.inventory_sales_monthly && this.productStats.inventory_sales_monthly.length > 0) {
					this.createColumnLineChart(this.$refs.chart_inventory_sales_monthly, this.productStats.inventory_sales_monthly, 'date', { value: 'price_on_hand', name: 'On Hand' }, { value: 'price_sold_ytd', name: 'Sold' }, 'month')
				}
				if (this.productStats.suppliers && this.productStats.suppliers.length > 0) {
					this.createColumnChart(this.$refs.chart_suppliers, this.productStats.suppliers, 'supplier', 'total_cost')
				}
				if (this.productStats.dealer_stock && this.productStats.dealer_stock.length > 0) {
					this.createPieChart(this.$refs.chart_dealer_stock, this.productStats.dealer_stock, 'label', 'value', false, true)
				}
				if (this.productStats.warehouse_orders && this.productStats.warehouse_orders.length > 0) {
					this.createBarChart(this.$refs.chart_warehouse_orders, this.productStats.warehouse_orders, 'distributor', 'total_ordered')
				}
				if (this.productStats.sales_by_type && this.productStats.sales_by_type.length > 0) {
					this.createPieChart(this.$refs.chart_sales_by_type, this.productStats.sales_by_type, 'type', 'value', true, true)
				}
			},
			getDealerList() {
				if (this.loadingDealerList) { return }
				this.loadingDealerList = true
				if (!this.filters.dealer_sort_column) {
					this.filters.dealer_sort_column = 'price_on_hand'
				}
				if (!this.filters.dealer_sort_order) {
					this.filters.dealer_sort_order = 'desc'
				}
				this.apiCall({ destination: 'get_list_dealers', data: this.filters }).then(obj => {
					this.loadingDealerList = false
					this.message = obj.message
					if (obj.status == 'success') {
						this.dealerList = obj.list
						this.dealerListPaging = obj.paging
					}
				}).catch(err => {
					console.log(err)
				})
			},
			getNonStockingDealerList() {
				if (this.loadingNonStockingDealerList) { return }
				this.loadingNonStockingDealerList = true
				if (!this.filters.non_stocking_dealer_list_page) {
					this.filters.non_stocking_dealer_list_page = 1
				}
				this.apiCall({ destination: 'get_list_non_stocking_dealers', data: this.filters }).then(obj => {
					this.loadingNonStockingDealerList = false
					this.message = obj.message
					if (obj.status == 'success') {
						this.nonStockingDealerList = obj.list
						this.nonStockingDealerListPaging = obj.paging
					}
				}).catch(err => {
					console.log(err)
				})

			},
			toggleLegend(which) {
				if (this.showLegend == which) {
					this.showLegend = ''
				} else {
					this.showLegend = which
				}
			},
			setSort(column) {
				if (this.filters.dealer_sort_column == column) {
					this.filters.dealer_sort_order = (this.filters.dealer_sort_order == 'asc' ? 'desc' : 'asc')
				} else {
					this.filters.dealer_sort_column = column
					this.filters.dealer_sort_order = 'desc'
				}
				this.filters.page = 1
				sessionStorage.setItem('filters', JSON.stringify(this.filters));
				this.getDealerList()
			},
			goToDealerListPage(page) {
				if (this.loadingDealerList) { return }
				this.filters.dealer_list_page = page
				this.getDealerList()
			},
			goToNonStockingDealerListPage(page) {
				if (this.loadingNonStockingDealerList) { return }
				this.filters.non_stocking_dealer_list_page = page
				this.getNonStockingDealerList()
			},
			goToDealer(dealer) {
				this.locationId = dealer.location_id // will instantiate <dealer> component
			},
			closeDealer() {
				this.updateSource('product')
				this.locationId = ''
			},
			backToReport() {
				this.$emit('close')
			}
		}
	}
</script>
